import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Card, IconButton } from '@mui/material'
import ArrowBack from 'assets/arrow_back.svg'
import PDFFileIcon from 'assets/icons/PDFFileIcon'
import { ROUTES } from '../../../../constants'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import { downloadFileFromData } from 'lib/helpers/utilities'
import { useAppSelector } from 'store/hooks'
import { resetForm } from 'actions/forms'
import AddIncomeCFDIContainer from 'containers/Invoices/CFDIs/AddIncomeCFDIContainer/AddIncomeCFDIContainer'
import Button from 'components/Shared/Button/Button'
import './styles.scss'

const AddIncomeCFDIScreen = () => {
  const { type } = useParams()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)

  const [PDFDocument, setPDFDocument] = useState<string | null>(null)
  const [isPDFViewer, setIsPDFViewer] = useState(false)

  const prevActiveWorkspaceRef = useRef(JSON.stringify(activeWorkspace))

  const updatePDFDocument = (pdf?: string) => {
    setPDFDocument(pdf ?? null)
  }

  const togglePDFViewer = () => {
    setIsPDFViewer(prevState => !prevState)
  }

  const downloadPDFHandler = (pdf: string | null) => {
    if (!pdf) return
    const linkSource = `data:application/pdf;base64,${pdf}`
    const fileName = 'prefactura.pdf'
    downloadFileFromData(fileName, linkSource)
  }

  const cleanUpAndLeave = useCallback(
    (backToInvoices: boolean = false) => {
      setTimeout(() => {
        backToInvoices ? navigate(ROUTES.INVOICES) : navigate(-1)
      }, 350)
    },
    [navigate]
  )

  const goBackHandler = () => {
    if (isPDFViewer) {
      setPDFDocument(null)
      togglePDFViewer()
      return
    }
    cleanUpAndLeave()
  }

  useEffect(() => {
    const serializedActiveWorkspace = JSON.stringify(activeWorkspace)
    if (prevActiveWorkspaceRef.current !== serializedActiveWorkspace) {
      cleanUpAndLeave(true)
      prevActiveWorkspaceRef.current = serializedActiveWorkspace
    }
  }, [cleanUpAndLeave, activeWorkspace])

  useEffect(
    () => () => {
      dispatch(resetForm('createInvoice'))
    },
    [dispatch]
  )

  if (!type || !['income', 'expense', '3', '4', '5', '6'].includes(type)) {
    return <Navigate to={ROUTES.INVOICES} replace />
  }

  if (!activeWorkspace) {
    return <Navigate to={ROUTES.HOME} replace />
  }

  return (
    <div className="screen-add-income-cfdi">
      <article className="title-container">
        {!isPDFViewer && <h2>Nuevo Comprobante de Ingresos 4.0</h2>}
        <Button
          className="back-button"
          variant="hover"
          color="secondary"
          startIcon={<img src={ArrowBack} alt="back" />}
          onClick={goBackHandler}
          disableRipple>
          Volver
        </Button>
      </article>
      <Card elevation={1} className="screen-add-invoice-content">
        {type === 'income' && (
          <>
            {isPDFViewer ? (
              <article className="pdf-view">
                <div className="header">
                  <div>
                    <span>Nuevo CFDI</span>
                    <h2>Vista previa</h2>
                  </div>
                  <IconButton
                    className="download-pdf"
                    onClick={() => downloadPDFHandler(PDFDocument)}
                    disableRipple>
                    <PDFFileIcon />
                  </IconButton>
                </div>
                <hr />
                <div className="pdf-viewer">
                  <object
                    aria-label="Visualizador de PDF"
                    data={`data:application/pdf;base64,${PDFDocument}`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  />
                </div>
              </article>
            ) : (
              <AddIncomeCFDIContainer
                updatePDFDocument={updatePDFDocument}
                togglePDFViewer={togglePDFViewer}
                downloadPDFHandler={downloadPDFHandler}
                cleanUpAndLeave={cleanUpAndLeave}
              />
            )}
          </>
        )}
      </Card>
    </div>
  )
}

export default AddIncomeCFDIScreen
